// Profit & loss comparison: the headline P&L numbers this period next to the
// equal-length period immediately before, computed client-side from one
// comparative P&L request (no new endpoint). Money is integer minor units.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Link } from "@tanstack/react-router";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import {
  type FinanceStatementResponse,
  getProfitAndLoss,
  type ReportBasis,
} from "@/lib/api";
import { pnlHeadline } from "./pnl-summary";
import { previousRange } from "./report-dates";
import { computeChange, formatPercent } from "./variance";
import { ReportError, ReportPeriod } from "./StatementTables";

const DISPLAY_CURRENCY = "USD";

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function money(cents: number | null): string {
  if (cents === null) return "—";
  return formatMoney({ cents, currency: DISPLAY_CURRENCY });
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`;
}

interface Query {
  start: string;
  end: string;
  basis: ReportBasis;
}

function ComparisonRow({
  label,
  current,
  previous,
  favorableWhenUp,
}: {
  label: string;
  current: number | null;
  previous: number | null;
  favorableWhenUp: boolean;
}) {
  const change = computeChange(current, previous, { favorableWhenUp });
  const tone =
    change === null || change.favorable === null
      ? "text-[var(--muted-foreground)]"
      : change.favorable
        ? "text-[var(--status-success-foreground)]"
        : "text-[var(--danger)]";
  return (
    <TableRow>
      <TableCell className="font-medium">{label}</TableCell>
      <TableCell className="text-right tabular-nums">{money(current)}</TableCell>
      <TableCell className="text-right tabular-nums">{money(previous)}</TableCell>
      <TableCell className={`text-right tabular-nums ${tone}`}>
        {change
          ? `${change.absoluteCents > 0 ? "+" : ""}${money(change.absoluteCents)}`
          : "—"}
      </TableCell>
      <TableCell className={`text-right tabular-nums ${tone}`}>
        {change ? formatPercent(change.percent) : "—"}
      </TableCell>
    </TableRow>
  );
}

export function PnlComparisonPage() {
  const [start, setStart] = useState(firstOfMonthIso);
  const [end, setEnd] = useState(todayIso);
  const [basis, setBasis] = useState<ReportBasis>("accrual");
  const [applied, setApplied] = useState<Query>({
    start: firstOfMonthIso(),
    end: todayIso(),
    basis: "accrual",
  });
  const [statement, setStatement] = useState<FinanceStatementResponse | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const run = useCallback((query: Query) => {
    const prior = previousRange(query.start, query.end);
    if (!prior) {
      setError("A valid start and end date are required.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    getProfitAndLoss({
      start_date: query.start,
      end_date: query.end,
      basis: query.basis,
      compare_from: prior.start,
      compare_to: prior.end,
    })
      .then(setStatement)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    run(applied);
  }, [applied, run]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!start || !end) {
      setError("Start and end dates are required.");
      return;
    }
    if (end < start) {
      setError("End date must be on or after the start date.");
      return;
    }
    setApplied({ start, end, basis });
  };

  const current = statement ? pnlHeadline(statement, 0) : null;
  const previous = statement ? pnlHeadline(statement, 1) : null;

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Profit & loss comparison"
        description="This period's revenue, expenses, and net income beside the equal-length period immediately before it."
        actions={
          <Button asChild variant="secondary">
            <Link to="/reports">All reports</Link>
          </Button>
        }
      />

      {error ? (
        <ReportError message={error} onRetry={() => run(applied)} />
      ) : null}

      <form
        onSubmit={submit}
        aria-label="Profit and loss comparison filters"
        className="surface flex flex-wrap items-end gap-3 rounded-2xl p-5"
      >
        <label htmlFor="plc-start" className="grid gap-1 text-xs font-medium">
          Start date
          <Input
            id="plc-start"
            type="date"
            value={start}
            onChange={(event) => setStart(event.target.value)}
            required
            disabled={loading}
          />
        </label>
        <label htmlFor="plc-end" className="grid gap-1 text-xs font-medium">
          End date
          <Input
            id="plc-end"
            type="date"
            value={end}
            onChange={(event) => setEnd(event.target.value)}
            required
            disabled={loading}
          />
        </label>
        <label htmlFor="plc-basis" className="grid gap-1 text-xs font-medium">
          Basis
          <select
            id="plc-basis"
            className="select"
            value={basis}
            onChange={(event) => setBasis(event.target.value as ReportBasis)}
            disabled={loading}
          >
            <option value="accrual">Accrual</option>
            <option value="cash">Cash</option>
          </select>
        </label>
        <Button type="submit" disabled={loading || !start || !end}>
          {loading ? "Running…" : "Run comparison"}
        </Button>
        <p className="w-full text-xs text-[var(--muted-foreground)]">
          The comparison period is filled in automatically: the same number of
          days immediately before the chosen range.
        </p>
      </form>

      {statement ? <ReportPeriod statement={statement} /> : null}

      {loading && statement === null && !error ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          Loading…
        </div>
      ) : statement && current && previous ? (
        <div className="surface overflow-hidden rounded-2xl">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Metric</TableHead>
                <TableHead className="text-right">This period</TableHead>
                <TableHead className="text-right">Previous period</TableHead>
                <TableHead className="text-right">Change</TableHead>
                <TableHead className="text-right">Change %</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <ComparisonRow
                label="Income"
                current={current.revenueCents}
                previous={previous.revenueCents}
                favorableWhenUp
              />
              <ComparisonRow
                label="Expenses"
                current={current.expensesCents}
                previous={previous.expensesCents}
                favorableWhenUp={false}
              />
              <ComparisonRow
                label="Net income"
                current={current.netIncomeCents}
                previous={previous.netIncomeCents}
                favorableWhenUp
              />
            </TableBody>
          </Table>
        </div>
      ) : !error && statement ? (
        <div className="surface rounded-2xl p-5 text-sm text-[var(--muted-foreground)]">
          No revenue or expenses were reported in either period.
        </div>
      ) : null}
    </div>
  );
}
